import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
    Alert,
    Box,
    Button,
    Chip,
    Container,
    Paper,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    CircularProgress,
    FormControlLabel,
} from "@mui/material";
import ClassOutlinedIcon from "@mui/icons-material/ClassOutlined";
import {
    fetchClassTermSubjects,
    fetchFormTeacherChangeRequests,
    reviewFormTeacherChangeRequest,
    setClassElectiveWindow,
} from "../../utils/termSubjectApi";
import { useFormTeacherHomeroom } from "../../hooks/useFormTeacherHomeroom";
import { isSubjectChoiceFormLevel } from "../../utils/form3SubjectSelection";

export default function FormTeacherTermSubjects() {
    const { currentUser } = useSelector((s) => s.user);
    const teacherId = currentUser?._id || currentUser?.id;
    const { homeroomClassId, homeroomLabel, hasHomeroomClass } = useFormTeacherHomeroom();

    const [loading, setLoading] = useState(true);
    const [classData, setClassData] = useState(null);
    const [requests, setRequests] = useState([]);
    const [message, setMessage] = useState("");
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        if (!homeroomClassId) {
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const [subjects, pending] = await Promise.all([
                fetchClassTermSubjects(homeroomClassId),
                fetchFormTeacherChangeRequests(teacherId),
            ]);
            setClassData(subjects);
            setRequests(Array.isArray(pending) ? pending : []);
        } catch (err) {
            setMessage(err.response?.data?.message || "Could not load class subjects.");
        } finally {
            setLoading(false);
        }
    }, [homeroomClassId, teacherId]);

    useEffect(() => {
        load();
    }, [load]);

    const electiveOpen = classData?.electiveWindow?.isOpen;
    const periodLabel = classData?.period
        ? [classData.period.term, classData.period.academicYear].filter(Boolean).join(" · ")
        : "";

    const handleElectiveToggle = async (open) => {
        if (!homeroomClassId) return;
        setSaving(true);
        try {
            await setClassElectiveWindow(homeroomClassId, { open, teacherId });
            setMessage(open ? "Elective selection opened for this term." : "Elective selection closed.");
            await load();
        } catch (err) {
            setMessage(err.response?.data?.message || "Could not update elective window.");
        } finally {
            setSaving(false);
        }
    };

    const handleReview = async (id, approve) => {
        try {
            await reviewFormTeacherChangeRequest(id, { reviewerId: teacherId, approve, note: approve ? "Approved" : "Rejected" });
            setMessage(approve ? "Forwarded to director for final approval." : "Request rejected.");
            await load();
        } catch (err) {
            setMessage(err.response?.data?.message || "Could not review request.");
        }
    };

    if (!hasHomeroomClass) {
        return (
            <Container sx={{ py: 4 }}>
                <Alert severity="info">You need a homeroom class to manage term subjects and electives.</Alert>
            </Container>
        );
    }

    if (loading && !classData) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                <CircularProgress />
            </Box>
        );
    }

    const showElective = isSubjectChoiceFormLevel(homeroomLabel);

    return (
        <Container maxWidth="lg" sx={{ py: 3 }}>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
                <ClassOutlinedIcon color="primary" />
                <Box>
                    <Typography variant="h5" fontWeight={700}>
                        Class subjects — {homeroomLabel}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Term-scoped teaching assignments for {periodLabel || "the current term"}.
                    </Typography>
                </Box>
            </Stack>

            {message ? (
                <Alert severity="info" sx={{ mb: 2 }} onClose={() => setMessage("")}>
                    {message}
                </Alert>
            ) : null}

            {showElective ? (
                <Paper variant="outlined" sx={{ p: 2.5, mb: 3, borderRadius: 3 }}>
                    <Typography fontWeight={700} gutterBottom>
                        Form 3 / Form 4 elective selection
                    </Typography>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={Boolean(electiveOpen)}
                                disabled={saving}
                                onChange={(e) => handleElectiveToggle(e.target.checked)}
                            />
                        }
                        label={electiveOpen ? "Open — pupils may choose subjects this term" : "Closed — pupils cannot pick electives"}
                    />
                </Paper>
            ) : null}

            <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden", mb: 3 }}>
                <Box sx={{ px: 2.5, py: 2, borderBottom: 1, borderColor: "divider" }}>
                    <Typography fontWeight={700}>Subject teachers this term</Typography>
                </Box>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Subject</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Teacher</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {(classData?.subjects || []).map((s) => (
                            <TableRow key={s._id || s.id}>
                                <TableCell sx={{ fontWeight: 600 }}>{s.subName}</TableCell>
                                <TableCell>
                                    <Chip size="small" label={s.isElective ? "Elective" : "Compulsory"} variant="outlined" />
                                </TableCell>
                                <TableCell>{s.teacher?.name || "Unassigned"}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Paper>

            <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
                <Box sx={{ px: 2.5, py: 2, borderBottom: 1, borderColor: "divider" }}>
                    <Typography fontWeight={700}>Pending subject change requests</Typography>
                </Box>
                {requests.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ p: 2.5 }}>
                        No pending requests from teachers in your class.
                    </Typography>
                ) : (
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Teacher</TableCell>
                                <TableCell>Subject</TableCell>
                                <TableCell>Reason</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {requests.map((r) => (
                                <TableRow key={r._id || r.id}>
                                    <TableCell>{r.requester?.name}</TableCell>
                                    <TableCell>{r.subject?.subName}</TableCell>
                                    <TableCell>{r.reason || "—"}</TableCell>
                                    <TableCell align="right">
                                        <Button size="small" onClick={() => handleReview(r._id || r.id, true)}>
                                            Approve
                                        </Button>
                                        <Button size="small" color="error" onClick={() => handleReview(r._id || r.id, false)}>
                                            Reject
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </Paper>
        </Container>
    );
}
